/**
 * Starting a request, five ways: a blank draft, a draft from catalog
 * templates, a draft from one of the firm's templates (through the template
 * send path with `send: false`, so the template's defaults come along), a
 * documents-only draft for a visa, or a brief handed to Casey (whose
 * `build_intake` opens the draft through `data-open-artifact`).
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type { OutgoingIntake } from "@/api/types/outgoingIntake";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { intakeArtifact } from "../model";
import type { NewRequestAction } from "../types";

/** Template ids whose questions are document requests, for a docs-only draft. */
const DOCS_TEMPLATE = "documents";

export const useNewRequest = () => {
  const { clientId, open, invalidate, requestPrompt } = useRequestsIde();
  const data = useRequestsData();
  const [busy, setBusy] = useState(false);

  const created = useCallback(
    (intake: OutgoingIntake) => {
      data.applyIntake(intake);
      invalidate(["intakes", "activity"]);
      open(intakeArtifact(intake));
    },
    [data, invalidate, open]
  );

  const createFromTemplates = useCallback(
    async (
      templateIds: string[],
      title?: string
    ): Promise<OutgoingIntake | null> => {
      setBusy(true);
      try {
        const intake = await OutgoingIntakesAPI.createForForeignNational(
          clientId,
          {
            template_ids: templateIds.length ? templateIds : ["custom"],
            ...(title ? { title } : {})
          }
        );
        created(intake);
        return intake;
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Couldn't create the request"
        );
        return null;
      } finally {
        setBusy(false);
      }
    },
    [clientId, created]
  );

  /** A draft from one of the firm's templates, defaults included. */
  const createFromSaved = useCallback(
    async (templateId: string): Promise<OutgoingIntake | null> => {
      setBusy(true);
      try {
        const result = await OutgoingIntakesAPI.sendSavedTemplate(templateId, {
          foreign_national_id: clientId,
          send: false
        });
        created(result.outgoing_intake);
        invalidate(["templates"]);
        return result.outgoing_intake;
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Couldn't create the request"
        );
        return null;
      } finally {
        setBusy(false);
      }
    },
    [clientId, created, invalidate]
  );

  const start = useCallback(
    async (action: NewRequestAction) => {
      switch (action.kind) {
        case "blank":
          await createFromTemplates([], "New request");
          return;
        case "templates":
          await createFromTemplates(action.templateIds);
          return;
        case "saved":
          await createFromSaved(action.templateId);
          return;
        case "docs":
          await createFromTemplates(
            [DOCS_TEMPLATE],
            `${action.visa} documents`
          );
          return;
        case "ai":
          requestPrompt(action.prompt ?? "Build an intake that ", {
            send: false
          });
          break;
        default:
          break;
      }
    },
    [createFromSaved, createFromTemplates, requestPrompt]
  );

  return { busy, start, createFromTemplates, createFromSaved };
};
